/** @jsx tsx */
import { tsx } from "@dojo/framework/core/vdom";
import { DNode } from "@dojo/framework/core/interfaces";
import * as _ from "lodash";
import { CURVE_SELECTION_STROKE_WIDTH, IMAGE_MARGIN, PERCENTAGE_HUNDRED, PERCENTAGE_ZERO } from "../../Constants";
import { isClosedShape } from "../../store/SelectionHelper";
import { ILineProperties } from "../../widgets/interfaces/model/properties/LineProperties";
import { IExtents } from "../interfaces/model/dataStructures/index";
import { FillTypes, GeometryTypes, LineCap, DashPresets } from "../interfaces/model/enumerations/index";
import { IShapeTextRectangle } from "../interfaces/model/geometry/ShapeTextRectangle";
import { IShape } from "../interfaces/model/PageObjects/Shape";
import { ISolidFillProperties } from "../interfaces/model/properties/FillProperties";
import { IPictureFillProperties } from "../interfaces/model/properties/index";
import { getShapeFill } from "../util";
import { MarkerObject } from "./MarkerObject";
import { IPageObjectRenderProps, PageObject } from "./PageObject";
import { ShapePath } from "./ShapePath";
import { ShapeTextBody } from "./ShapeTextBody";
import { SelectionConstants } from "../../store/reducers/index";
import { ICanvasEditor } from "../interfaces/model";
import { generateUUID } from "canvas/store/reducers/util";

/**
 * Shape renderer properties
 */
export interface IShapeRenderProps extends IPageObjectRenderProps<IShape> {
    /** Flag used for generating canvas snapshots */
    dontDownloadImages: boolean;

    /** Represents canvas editor objects */
    canvasEditor: ICanvasEditor;

    /** Shape path */
    path: string;

    /** Flag indicating multiselect */
    isMultiSelect: boolean;

    parentFlipH?: boolean;

    parentFlipV?: boolean;

    textRect: IShapeTextRectangle;

    /** Key for shape group */
    groupShapeKey?: string;

    /** Canvas container html element, if one is not set, it will default to document body */
    canvasContainerEl: HTMLElement;

    /** image load event */
    emitImagesLoadedEvent(): void;

    /** Callback to set shape content. */
    setShapeTextContent?(objectKey: number, content: string, selectedPageNumber: number, transient?: boolean): void;

    /** Callback to set text editor instance. */
    setTextEditorInstance(textEditorInstance: any): void;

    /**
     * Select object callback
     * @param selectedObject Object index in its parent child list
     * @param isMultiSelect Flag to indicate multi selection
     * @param transient Undo/redo stack flag
     * @param childId optionally select a subgroup element if selecting a group
     */
    subgroupSelect?(selectedObject: number, isMultiSelect?: boolean, transient?: boolean, childId?: number): void;

    /** Callback to set text style for format painter */
    setFormatPainterTextStyles(textStyles: any, valueStyles: any): void;

    /** Callback to set text first character styles */
    setFirstCharStyle(textStyles: any): void;

    /** Set source rectangle refExt */
    setSrcRectRefExt(refExt: IExtents): void;
}

/**
 * Shape stateless renderer
 */
export class Shape extends PageObject<IShapeRenderProps> {
	/**
     * Marker head id
     */
	private markerHeadID: string = "";

	/**
     * Marker tail id
     */
	private markerTailID: string = "";

	/**
     * Handle to the Timer on Touch Start Event
     * @type {any}
     */
	private tapHoldTimerHandle: any = null;

	/**
     * Callback to handle content change, forward call to action.
     * @param content {string} text content to be set.
     * @param selectedPageNumber {number} Selected page.
     */
	protected onContentChanged(content: string, selectedPageNumber: number, transient?: boolean) {
		if (this.properties.setShapeTextContent) {
			this.properties.setShapeTextContent(this.properties.key as number, content, selectedPageNumber, transient);
		}
	}

	/**
     * Whether the rendered shape has a highlight around it.
     * @param pageObject {IShape} current page object
     */
	protected pageObjectIsHighlighted(pageObject: IShape): boolean {
		const { highlightType } = this.properties;
		return (highlightType === SelectionConstants.IMAGE_PLACEHOLDER &&
            (pageObject.imagePlaceholder || pageObject.isPictureFromPlaceholder)) as boolean;
	}

	/**
    * Mouse enter event for the page object.
    * @param event {MouseEvent}
    */
	protected _onMouseEnter(event: MouseEvent) {
		const {
			absoluteLock,
			canvasContainerEl,
			key,
			pageObject,
			removePageDragOverClass,
		} = this.properties;
		if (!pageObject.attributeKey && this.pageObjectIsHighlighted(pageObject)) {
			removePageDragOverClass();
			const path = canvasContainerEl.querySelector("#path_" + key) as HTMLElement;
			if (pageObject.spPr.locked || absoluteLock) {
				path.setAttribute(SelectionConstants.STROKE, SelectionConstants.PRIMARY_RED);
			} else {
				path.setAttribute(SelectionConstants.STROKE, SelectionConstants.PRIMARY_BLUE);
			}
		}
	}

	/**
     * Mouse leave event for the page object.
     * @param event {MouseEvent}
     */
	protected _onMouseLeave(event: MouseEvent) {
		const {
			canvasContainerEl,
			addPageDragOverClass,
			key,
			pageObject,
		} = this.properties;
		if (!pageObject.attributeKey && this.pageObjectIsHighlighted(pageObject)) {
			addPageDragOverClass();
			const path = canvasContainerEl.querySelector("#path_" + key) as HTMLElement;
			path.setAttribute(SelectionConstants.STROKE, SelectionConstants.PRIMARY_GREY);
		}
	}

	/**
     * Add marker if line has headEnd and headTail properties
     * @return {MarkerObject | null}
     */
	protected renderDefs(): DNode {
		if (this.properties.pageObject.spPr.ln && !isClosedShape(this.properties.pageObject)) {
			const { headEnd, tailEnd } = this.properties.pageObject.spPr.ln;

			const isHead = (headEnd && headEnd !== LineCap.NO_ARROW);
			const isTail = (tailEnd && tailEnd !== LineCap.NO_ARROW);
			this.markerHeadID = isHead ? `${this.properties.pageObject.shapeId}_headID` : "";
			this.markerTailID = isTail ? `${this.properties.pageObject.shapeId}_tailID` : "";

			const lineProps = this.properties.pageObject.spPr.ln;

			return (
				<MarkerObject
					lineProps={lineProps}
					markerHeadID={this.markerHeadID}
					markerTailID={this.markerTailID}
				/>
			);
		} else {
			this.markerHeadID = "";
			this.markerTailID = "";
		}
		return null;
	}

	/**
     * Get image fill defs if picture fill is available.
     * @return Defs for image fill, null otherwise.
     */
	protected getImageFillDefs(): DNode {
		const { fill, xfrm } = this.properties.pageObject.spPr;
		if (!fill || fill.type !== FillTypes.PICTURE_FILL) {
			return null;
		}
		if (!fill.srcRect.refExt) {
			const refExt = _.cloneDeep(xfrm.ext);
			if (this.properties.geoBoundaryMeta) {
				refExt.cx = this.properties.geoBoundaryMeta.width;
				refExt.cy = this.properties.geoBoundaryMeta.height;
			}
			if (this.properties.isSelected) {
				this.properties.setSrcRectRefExt(refExt);
			}
		}
		const { blip, srcRect } = (fill as IPictureFillProperties);
		const cropHeight = PERCENTAGE_HUNDRED - (srcRect.b || PERCENTAGE_ZERO) - (srcRect.t || PERCENTAGE_ZERO);
		const cropWidth = PERCENTAGE_HUNDRED - (srcRect.r || PERCENTAGE_ZERO) - (srcRect.l || PERCENTAGE_ZERO);

		const { flipH, rot } = xfrm;

		// Transform scale for image
		let imageScale = "scale(1,1)";

		// Transform rotation for image as SVG attribute
		const imageRot = `rotate(${rot - srcRect.rot})`;

		// Transform rotation for image in CSS context
		const imageCSSRot = `rotate(${rot - srcRect.rot}deg)`;

		// Flip image only when srcRect and object flips are opposite
		if (flipH ? !srcRect.flipH : srcRect.flipH) {
			imageScale = "scale(-1,1)";
		}

		// Depending on whether we download the images or not, we will either assign dummy value data-csi-href
		// or real href this value
		const href = blip.link;

		// Note: with patternContentUnits="objectBoundingBox" set, image position (x and y) and dimensions
		// (height and width) will be in terms of 1 being 100%. So the computed values are divided by 100,
		// that too, without % sign suffixed.
		// Additionally, pattern and image tags are with preserveAspectRatio="none". Position and resize handling
		// mechanism would be responsible to maintain aspect ratio if required.
		// Also, it is assumed that while inserting the image on page, its dimensions are provided so the
		// enclosing shape would be set with that.

		// Transformation note:
		// For safari, CSS transform and transform origin as svg image attribute for transform and transform-origin is
		// not working, as on date.
		// For IE, svg image attribute for transform and transform origin as corresponding CSS version is not supported.
		// No need to deploy browser detection algorithm as browsers picks up what is supported and ignores what is not,
		// in this specific case.

		// Calculate values before conditional to not duplicate logic
		let height = this.properties.geoBoundaryMeta ? this.properties.geoBoundaryMeta.height : PERCENTAGE_HUNDRED;
		const id = this.properties.pageObject.shapeId;
		let width = this.properties.geoBoundaryMeta ? this.properties.geoBoundaryMeta.width : PERCENTAGE_HUNDRED;
		let x = this.properties.geoBoundaryMeta ? this.properties.geoBoundaryMeta.xMin : 0;
		let y = this.properties.geoBoundaryMeta ? this.properties.geoBoundaryMeta.yMin : 0;
		x = x - IMAGE_MARGIN;
		y = y - IMAGE_MARGIN;
		width = width + IMAGE_MARGIN * 2;
		height = height + IMAGE_MARGIN * 2;
		const transformString = imageCSSRot + " " + imageScale;
		const imageTransformString = "translate(0.5 0.5) " + imageRot + " " + imageScale + "translate(-0.5 -0.5)";
		const imageHeight = cropHeight / PERCENTAGE_HUNDRED;
		const imageWidth = cropWidth / PERCENTAGE_HUNDRED;
		let imageX = (srcRect.l || PERCENTAGE_ZERO) / PERCENTAGE_HUNDRED;
		let imageY = (srcRect.t || PERCENTAGE_ZERO) / PERCENTAGE_HUNDRED;
		imageX = imageX + IMAGE_MARGIN / width;
		imageY = imageY + IMAGE_MARGIN / height;

		if (this.properties.dontDownloadImages) {
			return (
				<defs>
					<pattern
						height={`${height}`}
						id={`${id}_image_fill_${this.imageFillUUID}`}
						patternContentUnits="objectBoundingBox"
						patternUnits="userSpaceOnUse"
						preserveAspectRatio="none"
						width={`${width}`}
						x={`${x}`}
						y={`${y}`}
					>
						<image
							data-csi-href={`${href}`}
							height={`${imageHeight}`}
							preserveAspectRatio="none"
							styles={{
								transform: `${transformString}`,
								transformOrigin: "0.5px 0.5px",
							}}
							transform={`${imageTransformString}`}
							transform-origin = "0.5 0.5"
							width={`${imageWidth}`}
							x={`${imageX}`}
							y={`${imageY}`}
						/>
					</pattern>
				</defs>
			);
		} else {
			return (
				<defs>
					<pattern
						height={`${height}`}
						id={`${id}_image_fill_${this.imageFillUUID}`}
						patternContentUnits="objectBoundingBox"
						patternUnits="userSpaceOnUse"
						preserveAspectRatio="none"
						width={`${width}`}
						x={`${x}`}
						y={`${y}`}
					>
						<image
							height={`${imageHeight}`}
							href={`${href}`}
							onload={this.properties.emitImagesLoadedEvent}
							preserveAspectRatio="none"
							styles={{
								transform: `${transformString}`,
								transformOrigin: "0.5px 0.5px",
							}}
							transform={`${imageTransformString}`}
							transform-origin = "0.5 0.5"
							width={`${imageWidth}`}
							x={`${imageX}`}
							y={`${imageY}`}
						/>
					</pattern>
				</defs>
			);
		}
	}

	/**
     * Render shape text
     */
	protected renderIfShapeTextBody(): DNode<ShapeTextBody> {
		const {
			canvasEditor,
			canvasMode,
			defaultTextStyles,
			deselectAllObjects,
			subgroupSelect,
			selectObjectById,
			groupShapeKey,
			isMultiSelect,
			isSelected,
			parentFlipH,
			parentFlipV,
			removeMigratedExtents,
			selectedPageId,
			setFirstCharStyle,
			setFormatPainterTextStyles,
			setObjectExtends,
			setTextEditorInstance,
			pageObject,
			textRect,
		} = this.properties;
		const { isPicture } = pageObject;
		if (!pageObject || !textRect || isPicture) {
			return null;
		}

		return (
			<ShapeTextBody
				canvasEditor={canvasEditor}
				canvasMode={canvasMode}
				defaultTextStyles={defaultTextStyles}
				deselectAllObjects={deselectAllObjects}
				groupShapeKey={groupShapeKey}
				isMultiSelect={isMultiSelect}
				isSelected={isSelected}
				isTableShape={pageObject.isTableShape}
				isTextShape={pageObject.isTextShape}
				onContentChanged={this.onContentChanged}
				onDblClick={this._onShapeDblClick}
				onMouseEnter={this._onMouseEnter}
				onMouseLeave={this._onMouseLeave}
				onPointerUp={this._onShapePointerUp}
				onSelect={this._onShapePointerDown}
				pageObject={pageObject}
				parentFlipH={parentFlipH}
				parentFlipV={parentFlipV}
				removeMigratedExtents={removeMigratedExtents}
				selectObject={subgroupSelect}
				selectObjectById={selectObjectById}
				selectedPageId={selectedPageId as string}
				setFirstCharStyle={setFirstCharStyle}
				setFormatPainterTextStyles={setFormatPainterTextStyles}
				setObjectExtends={setObjectExtends}
				setTextEditorInstance={setTextEditorInstance}
				shapeHeight={pageObject.spPr.xfrm.ext.cy}
				shapeId={pageObject.shapeId}
				shapeWidth={pageObject.spPr.xfrm.ext.cx}
				textBody={pageObject.txBody}
				textRect={textRect}
			/>
		);
	}

	/**
     * Timer to detect Tap Hold
     * @param {number} delay
     */
	protected tapHoldTimer(delay: number) {
		this.tapHoldTimerHandle = setTimeout(() => {
			const mouseEvent: any = {};
			this._onShapeDblClick( mouseEvent );
		}, delay);
	}

	/**
     * Render shape.
     */
	protected renderObject(): DNode {
		// key for the path element
		const dontDownloadImages = this.properties.dontDownloadImages;
		let key = `path_${this.properties.key}`;
		let pointerEvents = "stroke";
		if (dontDownloadImages) {
			key = `ddipath_${this.properties.key}`;
			pointerEvents = "none";
		}
		// fill value to object
		const fill = getShapeFill(this.properties.pageObject.shapeId,
			this.properties.pageObject.spPr.fill, this.imageFillUUID);
		// set stroke color value to object
		const lineProps = _.cloneDeep(this.properties.pageObject.spPr.ln as ILineProperties);
		const lineFill = lineProps && lineProps.fill as ISolidFillProperties;
		let highlight = false;

		if (!this.properties.pageObject.attributeKey && this.pageObjectIsHighlighted(this.properties.pageObject)) {
			lineProps.dash = DashPresets.DASH;
			lineFill.color = "grey";
			lineProps.W = 2;
			highlight = true;
		}

		const shapePath = (
			<ShapePath
				dontDownloadImages={dontDownloadImages}
				fill={fill}
				highlight={highlight}
				key={key}
				lineFill={lineFill}
				lineProps={lineProps}
				markerHeadID={this.markerHeadID}
				markerTailID={this.markerTailID}
				ondblclick={this._onShapeDblClick}
				onmouseenter={this._onMouseEnter}
				onmouseleave={this._onMouseLeave}
				onpointerdown={this._onShapePointerDown}
				onpointerup={this._onShapePointerUp}
				path={this.properties.path}
				strokeSelect={isClosedShape(this.properties.pageObject)}
			/>
		);

		const ghostShape = (
			<path
				d={`${this.properties.path}`}
				fill="none"
				key="curveShapeForSelection"
				ondblclick= {this._onShapeDblClick}
				onmouseenter= {this._onMouseEnter}
				onmouseleave= {this._onMouseLeave}
				onpointerdown={this._onShapePointerDown}
				onpointerup={this._onShapePointerUp}
				ontouchend={() => { clearTimeout(this.tapHoldTimerHandle); }}
				ontouchmove={() => { clearTimeout(this.tapHoldTimerHandle); }}
				ontouchstart={() => { this.tapHoldTimer(1000); }}
				pointer-events={pointerEvents}
				stroke="none"
				stroke-width={`${CURVE_SELECTION_STROKE_WIDTH}`}
				vector-effect="non-scaling-stroke"
			/>
		);

		const spPr = this.properties.pageObject.spPr;
		return spPr.geometry.type === GeometryTypes.CUSTOM ? (<g>{shapePath}{ghostShape}</g>) : (shapePath);
	}
}

export default Shape;
